<script setup lang="ts">
  import settingsIcon from '@assets/icons/sidebar/settings.svg'
  import AIIcon from '@assets/icons/sidebar/ai_icon.svg'
  import prescriptionIcon from '@assets/icons/prescription.svg'
  import AboutUsIcon from '@assets/icons/sidebar/about_us.svg'
  import LogOutIcon from '@assets/icons/sidebar/log_out.svg'
  import LinkIcon from '@assets/icons/sidebar/link.svg'
  import { storeToRefs } from 'pinia'
  import { useAuthStore } from '@stores/auth.ts'
  import { useI18n } from 'vue-i18n'
  import { useSettingsDialog } from '@/composables/useSettingsDialog.ts'

  const { t } = useI18n()
  const authStore = useAuthStore()
  const { user, profileCompletion } = storeToRefs(authStore)
  const { show: showSettings } = useSettingsDialog()
  const paymentDialog = usePaymentDialog()

  const { collapsed } = defineProps<{
    collapsed: boolean,
  }>()

  const emits = defineEmits<{
    'show-logout': []
  }>()
</script>

<template>
  <v-menu
    class="user-main-menu"
    location="top"
    offset="12"
    width="295"
  >
    <template #activator="{ props }">
      <v-btn
        v-track="{event:'profile_clicked',eventData: {source: 'side_menu'}}"
        v-bind="props"
        :active="false"
        bg-active="red"
        class="user-avatar-wrapper d-flex justify-start align-center pa-2 rounded-lg flex-0-0"
        :class="{
          'w-100': !collapsed
        }"
        density="compact"
        :height="collapsed ? 50 : 70"
        min-width="50"
        variant="text"
      >
        <div class="d-flex justify-space-between align-center ga-2" :class="{'w-100': !collapsed }">
          <div class="d-flex justify-start align-center ga-2">
            <v-avatar border color="grey-300" size="32">
              <v-img v-if="user?.avatar_media?.url" :src="user.avatar_media.url" />
              <v-icon v-else icon="mdi-account-outline" size="24" />
            </v-avatar>
            <div v-if="!collapsed">
              <div class="text-start text-body-2 font-weight-medium text-grey-50">
                {{ user?.name ?? t('empty_name') }}
              </div>
              <div v-if="! authStore.isSubscribed" class="text-start text-caption font-weight-medium text-grey-300">
                {{ t('not_subscribed') }}
              </div>
            </div>
          </div>
          <v-icon v-if="!collapsed" icon="mdi-chevron-left" size="20" />
        </div>
      </v-btn>
    </template>

    <v-list class="pt-1 px-1 pb-3 rounded-xl bg-menu" min-width="295">
      <v-list-item
        v-if="profileCompletion < 100"
        :active="false"
        class="pt-3 rounded-sm"
        @click="showSettings()"
      >
        <div class="flex-grow-1 font-weight-medium">
          <div class="text-body-2">{{ t('complete_profile_info') }}</div>
          <div class="text-caption text-grey-400">{{ t('one_step_remained') }}</div>
        </div>
        <template #append>
          <v-btn class="px-3 text-caption	font-weight-bold" color="primary" variant="flat">
            {{ t('complete_profile') }}
          </v-btn>
        </template>
      </v-list-item>
      <v-list-item v-if="profileCompletion < 100" class="py-0 rounded-sm" height="20" style="min-height: 20px;">
        <v-progress-linear
          bg-color="grey-600"
          class="rounded-pill"
          color="link-color"
          :height="4"
          :model-value="profileCompletion"
        />
      </v-list-item>
      <v-list-item
        v-if="! authStore.isSubscribed"
        v-track="{event:'sina_pro_clicked', eventData: {source: 'side_menu'}}"
        :active="false"
        class="rounded-sm"
        :prepend-icon="AIIcon"
        :title="t('subscribe_now')"
        @click="paymentDialog.show()"
      />
      <v-list-item
        v-track="{
          event:'consultations_list_clicked',
          eventData: {source: 'side_menu'}
        }"
        :active="false"
        class="rounded-sm"
        :prepend-icon="prescriptionIcon"
        :title="t('my_previous_consultations')"
        :to="{ name: 'profile-consultations'}"
      />
      <v-list-item
        :active="false"
        class="rounded-sm"
        :prepend-icon="settingsIcon"
        :title="t('settings')"
        @click="showSettings()"
      />
      <v-divider class="my-2 mx-4" color="grey-600" />

      <v-menu location="end top">
        <template #activator="{ props }">
          <v-list-item
            v-bind="props"
            :active="false"
            append-icon="mdi-chevron-left"
            class="rounded-sm"
            :prepend-icon="AboutUsIcon"
            :title="t('about_us')"
          />
        </template>

        <v-list class="rounded-xl bg-menu pa-1" min-width="287">
          <v-list-item
            :active="false"
            :append-icon="LinkIcon"
            class="rounded-sm"
            :title="t('privacy_policy')"
            :to="{ name: 'content-pages-slug', params: { slug: 'privacy-policy' } }"
          />

          <v-list-item
            :active="false"
            :append-icon="LinkIcon"
            class="rounded-sm"
            :title="t('terms_and_conditions')"
            :to="{ name: 'content-pages-slug', params: { slug: 'terms-and-conditions' } }"
          />

          <v-divider class="my-2 mx-4" color="grey-600" />

          <v-list-item
            :active="false"
            class="rounded-sm"
            href="mailto:wecare@altibbi.com"
            :title="t('contact_us')"
          />
        </v-list>
      </v-menu>

      <v-list-item
        class="rounded-sm"
        :prepend-icon="LogOutIcon"
        :title="t('logout')"
        @click="emits('show-logout')"
      />
    </v-list>
  </v-menu>
</template>

<style scoped lang="scss">
.user-avatar-wrapper {
  :deep(.v-btn__content) {
    width: 100%;
  }

  &[aria-expanded="true"] {
    background-color: rgb(var(--v-theme-grey-800));

    :deep(.v-btn__overlay) {
      display: none;
    }
  }
}

:deep(.v-progress-linear__background) {
  opacity: 1;
}

.user-main-menu {
  :deep(.v-list-item-title) {
    font-size: 0.875rem;
    font-weight: 500;
  }

  :deep(.v-list-item__prepend) {
    width: 32px;
  }

  :deep(.v-list-item--density-default.v-list-item--one-line) {
    min-height: 40px;
  }


  :deep(.v-list) {
    box-shadow: 0 24px 48px -12px rgba(19, 19, 19, 0.20);
  }
}
</style>
